import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  buildProcessHandler,
  FadeDivider,
  FILE_FORMATS,
  FormProvider,
  OptionalChip,
  RequiredChip,
  RHFLanguage,
  RhfMediaInlineUpload,
  RHFPhoneInput,
  RHFProfessionAutocomplete,
  RHFSubmitBtn,
  RHFTextField,
  RHFTimezone,
  useConfirmService,
} from '@onesource/components';
import {
  EImageVariant,
  ProfileFormSchema,
  TProfileFormValues,
  TUpdateProfile,
  USERNAME_REGEX,
} from '@onesource/schemas';
import {
  getEmailChangePendingQueryKey,
  useCancelEmailChangeMutation,
  useCheckUsernameAvailableQuery,
  useEmailChangePendingQuery,
  useGetCitiesQuery,
  useGetCountriesQuery,
  useGetStatesQuery,
  useUpdateProfileMutation,
} from '@onesource/services';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { MdAccessTime, MdClose, MdEdit, MdFacebook, MdLanguage, MdLocationOn, MdSave, MdWork } from 'react-icons/md';
import { SiInstagram, SiLinkedin, SiTiktok, SiX, SiYoutube, SiZoom } from 'react-icons/si';
import { TfiEmail } from 'react-icons/tfi';
import { SHOW_DEV_TOOLS } from 'src/config';
import { z } from 'zod';

import { ChangeEmailDialog } from '../components/change-email-dialog';
import { AccountProfilePrivacyMode } from './account-profile-privacy-mode';
import { AccountProfileUsernameField } from './account-profile-username-field';

// ----------------------------------------------------------------------

const avatarProcessHandler = buildProcessHandler([EImageVariant.w400]);

// State/City are only required when the selected country actually has states
// available — some countries (Gibraltar, Macau, etc.) have none in our
// geography tables. That check depends on the live `states`/`cities` query
// results, so it can't live in this static schema; see the setError/clearErrors
// effects below instead, which mirror the username-availability pattern.
const ProfileForm = ProfileFormSchema.superRefine((value, ctx) => {
  if (value.address.countryId === null || value.address.countryId === undefined) {
    ctx.addIssue({ path: ['address', 'countryId'], code: z.ZodIssueCode.custom, message: 'Country is required' });
  }
});

const EMPTY_ADDRESS = { city: '', state: '', country: '', street: '', countryId: null, stateId: null, cityId: null };
const EMPTY_SOCIALS = { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '', tiktok: '' };

export type TProfileTabAddress = {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  street?: string | null;
  countryId?: number | null;
  stateId?: number | null;
  cityId?: number | null;
};

export type TProfileTabSocials = {
  facebook?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  tiktok?: string | null;
};

export type TProfileTabProps = {
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string | null;
  displayName?: string;
  username?: string | null;
  timezone?: string | null;
  occupation?: string | null;
  bio?: string | null;
  primaryLanguage?: string | null;
  phoneNumber?: string | null;
  zoomEmail?: string | null;
  address?: TProfileTabAddress | null;
  socials?: TProfileTabSocials | null;
  isPrivate?: boolean;
};

export const ProfileTab: FC<TProfileTabProps> = props => {
  const {
    firstName,
    lastName,
    email,
    avatar,
    displayName,
    username,
    timezone,
    occupation,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    isPrivate,
  } = props;

  const { mutateAsync, isPending } = useUpdateProfileMutation();

  // The login email is always read-only here and changed only via the
  // verification dialog + in-app pending banner (secure email-change flow).
  const queryClient = useQueryClient();
  const { setConfirm } = useConfirmService();

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const { data: emailChangePending } = useEmailChangePendingQuery();
  const isEmailChangePending = !!emailChangePending?.pending;

  // `blockedUntil` is a fixed timestamp, so the remaining time is recomputed on a
  // tick rather than read from the payload — the status query only polls every
  // 60s, so relying on the server's `canRequestAnother` alone would leave the
  // button disabled for up to a minute after the window actually lapsed.
  const [now, setNow] = useState(() => Date.now());

  const blockedSecondsLeft =
    isEmailChangePending && emailChangePending?.blockedUntil
      ? Math.max(0, Math.ceil((new Date(emailChangePending.blockedUntil).getTime() - now) / 1000))
      : 0;

  // A pending change outlives its short block window — its verification link
  // stays valid longer. Only block the button while the retry window is closed.
  const isEmailChangeBlocked = isEmailChangePending && blockedSecondsLeft > 0;

  useEffect(() => {
    if (!isEmailChangeBlocked) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isEmailChangeBlocked]);

  const emailChangeBlockedMessage = isEmailChangeBlocked
    ? `You just requested a change. You can request another in ${blockedSecondsLeft}s, or cancel the pending one above.`
    : '';

  const cancelEmailChangeMutation = useCancelEmailChangeMutation();

  const handleCancelEmailChange = () => {
    const cancelToken = emailChangePending?.cancelToken;
    if (!cancelToken) {
      return;
    }
    setConfirm({
      open: true,
      title: 'Cancel email change?',
      message:
        'This will cancel the pending change and, as a safety step, send a password-reset link to your current email. Your login email stays the same.',
      confirmBtnLabel: 'Cancel change',
      onConfirm: async () => {
        try {
          await cancelEmailChangeMutation.mutateAsync({
            token: cancelToken,
            resetRedirectUrl: `${window.location.origin}/auth/reset-password`,
          });
          await queryClient.invalidateQueries({ queryKey: getEmailChangePendingQueryKey() });
          enqueueSnackbar('Email change cancelled', {
            variant: 'success',
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          });
        } catch {
          enqueueSnackbar('Could not cancel the email change. Please try again.', {
            variant: 'error',
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          });
        }
      },
    });
  };

  const methods = useForm<TProfileFormValues>({
    resolver: zodResolver(ProfileForm),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      displayName: '',
      username: '',
      occupation: '',
      timezone: '',
      isPrivate: isPrivate ?? false,
      avatar: '',
      bio: '',
      primaryLanguage: '',
      phoneNumber: '',
      zoomEmail: '',
      address: EMPTY_ADDRESS,
      socials: EMPTY_SOCIALS,
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { isDirty, errors },
  } = methods;

  const watchedCountryId = useWatch({ control, name: 'address.countryId' });
  const watchedStateId = useWatch({ control, name: 'address.stateId' });
  const watchedCityId = useWatch({ control, name: 'address.cityId' });

  const { data: countries = [] } = useGetCountriesQuery();
  const { data: states = [], isFetched: isStatesFetched } = useGetStatesQuery({ countryId: watchedCountryId });
  // Countries with no states on file (Gibraltar, Macau, etc.) let cities be
  // picked directly by country instead — see GeographyController.getCities.
  const countryHasNoStates = isStatesFetched && states.length === 0;
  const { data: cities = [], isFetched: isCitiesFetched } = useGetCitiesQuery(
    countryHasNoStates ? { countryId: watchedCountryId } : { stateId: watchedStateId },
  );

  // State/City required only when the selected country has states/cities to
  // pick from — some countries (Gibraltar, Macau, etc.) have neither.
  useEffect(() => {
    if (!watchedCountryId || !isStatesFetched) {
      clearErrors('address.stateId');
      return;
    }
    if (states.length > 0 && !watchedStateId) {
      setError('address.stateId', { type: 'manual', message: 'State / Region is required' });
    } else {
      clearErrors('address.stateId');
    }
  }, [watchedCountryId, isStatesFetched, states.length, watchedStateId, setError, clearErrors]);

  useEffect(() => {
    if (!isCitiesFetched || (!watchedStateId && !countryHasNoStates)) {
      clearErrors('address.cityId');
      return;
    }
    if (cities.length > 0 && !watchedCityId) {
      setError('address.cityId', { type: 'manual', message: 'City is required' });
    } else {
      clearErrors('address.cityId');
    }
  }, [watchedStateId, countryHasNoStates, isCitiesFetched, cities.length, watchedCityId, setError, clearErrors]);

  const prevCountryIdRef = useRef<number | null>(null);

  useEffect(() => {
    const prevId = prevCountryIdRef.current;
    prevCountryIdRef.current = watchedCountryId ?? null;
    if (!watchedCountryId || watchedCountryId === prevId) {
      return;
    }
    const currentPhone = getValues('phoneNumber') ?? '';
    const isCodeOnly = /^\+\d{1,4}\s*$/.test(currentPhone.trim()) || currentPhone.trim() === '';
    if (!isCodeOnly) {
      return;
    }
    const country = countries.find(c => c.id === watchedCountryId);
    if (country?.phonecode) {
      setValue('phoneNumber', `+${country.phonecode}`);
    }
  }, [watchedCountryId, countries, setValue, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced username availability check (mirrors the pro-directory slug check).
  const usernameValue = watch('username');
  const [debouncedUsername, setDebouncedUsername] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUsername((usernameValue ?? '').trim().toLowerCase()), 400);
    return () => clearTimeout(timer);
  }, [usernameValue]);

  const isUsernameFormatValid = USERNAME_REGEX.test(debouncedUsername);
  const isUsernameUnchanged = debouncedUsername === (username ?? '').toLowerCase();
  const shouldCheckUsername = debouncedUsername.length > 0 && isUsernameFormatValid && !isUsernameUnchanged;

  const { data: usernameAvailability, isFetching: isCheckingUsername } = useCheckUsernameAvailableQuery(
    shouldCheckUsername ? debouncedUsername : '',
  );

  const isUsernameTaken = shouldCheckUsername && usernameAvailability?.available === false;
  const isUsernameAvailable = shouldCheckUsername && usernameAvailability?.available === true;

  useEffect(() => {
    if (isUsernameTaken) {
      setError('username', { type: 'manual', message: 'Username is already taken' });
    } else {
      clearErrors('username');
    }
  }, [isUsernameTaken, setError, clearErrors]);

  useEffect(() => {
    reset({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      displayName: displayName ?? '',
      username: username ?? '',
      occupation: occupation ?? '',
      timezone: timezone ?? '',
      isPrivate: isPrivate ?? false,
      avatar: avatar ?? '',
      bio: bio ?? '',
      primaryLanguage: primaryLanguage ?? '',
      phoneNumber: phoneNumber ?? '',
      zoomEmail: zoomEmail ?? '',
      address: {
        city: address?.city ?? '',
        state: address?.state ?? '',
        country: address?.country ?? '',
        street: address?.street ?? '',
        countryId: address?.countryId ?? null,
        stateId: address?.stateId ?? null,
        cityId: address?.cityId ?? null,
      },
      socials: {
        facebook: socials?.facebook ?? '',
        instagram: socials?.instagram ?? '',
        youtube: socials?.youtube ?? '',
        twitter: socials?.twitter ?? '',
        linkedin: socials?.linkedin ?? '',
        tiktok: socials?.tiktok ?? '',
      },
    });
  }, [
    firstName,
    lastName,
    email,
    displayName,
    username,
    timezone,
    occupation,
    avatar,
    isPrivate,
    bio,
    primaryLanguage,
    phoneNumber,
    zoomEmail,
    address,
    socials,
    reset,
  ]);

  const onSubmit = handleSubmit(async data => {
    const payload: TUpdateProfile & { avatar?: string } = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      displayName: data.displayName,
      username: data.username || undefined,
      occupation: data.occupation,
      timezone: data.timezone,
      // Privacy saves on its own via the privacy toggle; resend the current
      // value so a profile save never resets it (the schema defaults it to false).
      isPrivate: isPrivate ?? false,
      avatar: data.avatar,
      bio: data.bio,
      primaryLanguage: data.primaryLanguage,
      phoneNumber: data.phoneNumber,
      zoomEmail: data.zoomEmail,
      address: {
        ...data.address,
        // Always derive names from the live lookup at submit time — the IDs are
        // the source of truth, so stored strings never drift from them.
        country: countries.find(c => c.id === data.address.countryId)?.name ?? null,
        state: states.find(s => s.id === data.address.stateId)?.name ?? null,
        city: cities.find(c => c.id === data.address.cityId)?.name ?? null,
      },
      socials: data.socials,
    };
    await mutateAsync(payload);
  });

  return (
    <Box sx={{ py: 0 }}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <LinearProgress sx={{ mb: 2, borderRadius: 1, visibility: isPending ? 'visible' : 'hidden' }} />
        {isEmailChangePending && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                color="info"
                variant="soft"
                startIcon={<MdClose size={16} />}
                onClick={handleCancelEmailChange}
                disabled={cancelEmailChangeMutation.isPending || !emailChangePending?.cancelToken}
              >
                {cancelEmailChangeMutation.isPending ? 'Cancelling…' : 'Cancel'}
              </Button>
            }
          >
            We sent a verification link to <strong>{emailChangePending?.newEmail}</strong>. Your current email stays
            active for login until you confirm it.
            {emailChangePending?.expiresAt
              ? ` The link expires at ${new Date(emailChangePending.expiresAt).toLocaleString()}.`
              : ''}
          </Alert>
        )}
        <Grid container spacing={3}>
          {/* Avatar upload */}
          <Grid item xs={12} sm="auto">
            <Stack spacing={2} alignItems="center">
              <Controller
                name="avatar"
                control={control}
                render={({ field, fieldState }) => (
                  <RhfMediaInlineUpload
                    value={field.value}
                    onChange={field.onChange}
                    label="Avatar image"
                    processHandler={avatarProcessHandler}
                    allowedFormats={FILE_FORMATS.IMAGE}
                    selectVariant={EImageVariant.w400}
                    uploadVariants={[EImageVariant.w400]}
                    cropAspectRatio={1}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  First Name <RequiredChip />
                </Typography>
                <RHFTextField name="firstName" placeholder="First Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Last Name <RequiredChip />
                </Typography>
                <RHFTextField name="lastName" placeholder="Last Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Display Name <RequiredChip />
                </Typography>
                <RHFTextField name="displayName" placeholder="Display Name" disabled={isPending} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AccountProfileUsernameField
                  name="username"
                  disabled={isPending}
                  isChecking={isCheckingUsername}
                  isAvailable={isUsernameAvailable}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AccountProfilePrivacyMode disabled={isPending} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <FadeDivider sx={{ mt: 3 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Bio (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              Bio
              <OptionalChip />
            </Typography>
            <RHFTextField
              name="bio"
              placeholder="A short intro visible on your profile (up to 300 characters)."
              multiline
              minRows={3}
              disabled={isPending}
              helperText="Share what brings you here or your experience."
            />
          </Box>

          {/* Email — always read-only; changed only via the secure verification
              dialog + in-app pending banner, never saved with the profile form. */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TfiEmail /> Email <RequiredChip />
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <RHFTextField
                  name="email"
                  placeholder="Email Address"
                  disabled
                  InputProps={{ readOnly: true }}
                  endAdornment={
                    // Tooltip needs a non-disabled wrapper — MUI disabled
                    // buttons don't emit the pointer events it listens for.
                    <Tooltip title={emailChangeBlockedMessage} arrow>
                      <span>
                        <Button
                          variant="soft"
                          onClick={() => setEmailDialogOpen(true)}
                          disabled={isPending || isEmailChangeBlocked}
                          startIcon={<MdEdit size={18} />}
                        >
                          Change
                        </Button>
                      </span>
                    </Tooltip>
                  }
                />
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Primary account email for login, notifications, and support. Changing it requires verification.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <SiZoom size={18} />
              Zoom Email
              <OptionalChip />
            </Typography>
            <RHFTextField
              name="zoomEmail"
              helperText="You will be required to login to YOUR Zoom account prior to joining the lives calls. If this is not correct, you will not be able to join."
              placeholder="Zoom Email"
              disabled={isPending}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MdWork size={18} />
              Profession <RequiredChip />
            </Typography>
            <RHFProfessionAutocomplete name="occupation" disabled={isPending} />
          </Box>

          {/* Primary language / Zoom email */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MdLanguage size={18} />
              Primary Language <RequiredChip />
            </Typography>
            <RHFLanguage name="primaryLanguage" placeholder="Primary Language" disabled={isPending} />
          </Box>

          {/* Address: Country / State / City / Street (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <FadeDivider />
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MdLocationOn size={18} />
              Location
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Country <RequiredChip />
                </Typography>
                <Controller
                  name="address.countryId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={countries}
                      getOptionLabel={o => o.name}
                      value={countries.find(c => c.id === field.value) ?? null}
                      onChange={(_, option) => {
                        field.onChange(option?.id ?? null);
                        setValue('address.stateId', null, { shouldDirty: true });
                        setValue('address.cityId', null, { shouldDirty: true });
                      }}
                      disabled={isPending}
                      renderOption={(props, option) => (
                        // oxlint-disable-next-line react/jsx-key
                        <li {...props} key={option.id}>
                          {option.flag && <span style={{ marginRight: 8 }}>{option.flag}</span>}
                          {option.name}
                        </li>
                      )}
                      renderInput={params => {
                        const selected = countries.find(c => c.id === field.value);
                        return (
                          <TextField
                            {...params}
                            placeholder="Select your Country"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: selected?.flag ? (
                                <>
                                  <span style={{ marginRight: 4, fontSize: 18, lineHeight: 1 }}>{selected.flag}</span>
                                  {params.InputProps.startAdornment}
                                </>
                              ) : (
                                params.InputProps.startAdornment
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  State / Region {isStatesFetched && states.length === 0 ? <OptionalChip /> : <RequiredChip />}
                </Typography>
                <Controller
                  name="address.stateId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={states}
                      getOptionLabel={o => o.name}
                      value={states.find(s => s.id === field.value) ?? null}
                      onChange={(_, option) => {
                        field.onChange(option?.id ?? null);
                        setValue('address.cityId', null, { shouldDirty: true });
                      }}
                      disabled={isPending || !watchedCountryId || (isStatesFetched && states.length === 0)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder={
                            isStatesFetched && states.length === 0
                              ? 'No states/regions for this country'
                              : 'Select your State / Region'
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  City {isCitiesFetched && cities.length === 0 ? <OptionalChip /> : <RequiredChip />}
                </Typography>
                <Controller
                  name="address.cityId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={cities}
                      getOptionLabel={o => o.name}
                      value={cities.find(c => c.id === field.value) ?? null}
                      onChange={(_, option) => field.onChange(option?.id ?? null)}
                      disabled={
                        isPending ||
                        (!watchedStateId && !countryHasNoStates) ||
                        (isCitiesFetched && cities.length === 0)
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder={
                            isCitiesFetched && cities.length === 0
                              ? `No cities for this ${countryHasNoStates ? 'country' : 'state/region'}`
                              : 'Select your City'
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message ?? "Contact us if you don't see your city"}
                        />
                      )}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Address <OptionalChip />
                </Typography>
                <RHFTextField name="address.street" placeholder="Neighbourhood / Street address" disabled={isPending} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MdAccessTime size={18} />
                  Timezone <RequiredChip />
                </Typography>
                <RHFTimezone name="timezone" placeholder="Time Zone" disabled={isPending} />
              </Box>
            </Box>
          </Box>

          {/* Phone Number (full row) — after address, code auto-fills from selected country */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Phone Number
              <OptionalChip />
            </Typography>
            <RHFPhoneInput
              name="phoneNumber"
              placeholder="Phone Number"
              helperText="For verification or urgent support only — never shared publicly."
              disabled={isPending}
            />
          </Box>

          {/* Social Networks (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              Social Networks
              <OptionalChip />
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <RHFTextField
                name="socials.facebook"
                placeholder="https://facebook.com/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <MdFacebook size={20} style={{ marginRight: 8, color: '#1877F2', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.instagram"
                placeholder="https://instagram.com/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiInstagram size={20} style={{ marginRight: 8, color: '#E1306C', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.youtube"
                placeholder="https://youtube.com/@yourchannel"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiYoutube size={20} style={{ marginRight: 8, color: '#FF0000', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.twitter"
                placeholder="https://x.com/yourhandle"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiX size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.linkedin"
                placeholder="https://linkedin.com/in/yourprofile"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiLinkedin size={20} style={{ marginRight: 8, color: '#0A66C2', flexShrink: 0 }} />,
                }}
              />
              <RHFTextField
                name="socials.tiktok"
                placeholder="https://tiktok.com/@yourhandle"
                disabled={isPending}
                InputProps={{
                  startAdornment: <SiTiktok size={20} style={{ marginRight: 8, flexShrink: 0 }} />,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            // Above page content so form fields scroll under it, but well below
            // the header (1100) and sidebar (1101) so it never covers chrome.
            zIndex: 2,
            bgcolor: 'rgb(244, 239, 238)',
            display: 'flex',
            justifyContent: 'flex-end',
            py: 2,
            mt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <RHFSubmitBtn
            buttonProps={{
              disabled:
                isPending ||
                !isDirty ||
                isUsernameTaken ||
                isCheckingUsername ||
                !!errors.address?.stateId ||
                !!errors.address?.cityId,
              startIcon: isPending ? <CircularProgress size={16} color="inherit" /> : <MdSave />,
            }}
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </RHFSubmitBtn>
        </Box>
        {SHOW_DEV_TOOLS && <DevTool id="account-profile" control={methods.control} />}
      </FormProvider>

      <ChangeEmailDialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} currentEmail={email ?? ''} />
    </Box>
  );
};
